import { CoveragePlanType } from '@cms-max/medicare-api-sdk'
import { IconCheckShield } from 'components/Icon/IconCheckShield'
import { MyLink, MyLinkClickHandler } from 'components/MyLink'
import { useEvent } from 'events/useEvent'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { usePlansQuery } from 'queries/plans'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NoPlansCard } from '../NoPlansCard/NoPlansCard'
import { PartAPartBPlanCard } from '../PlanCard/PartAPartBPlanCard'
import { PlanCard } from '../PlanCard/PlanCard'
import { PlanCardSkeleton } from '../PlanCard/PlanCardSkeleton'
import { PlansErrorAlert } from '../PlansErrorAlert/PlansErrorAlert'

import classes from './CurrentPlans.module.scss'
import { useCombinedPartAPartB } from './useCombinedPartAPartB'

type CurrentPlansProps = {
  className?: string
}
export const CurrentPlans = ({ className }: CurrentPlansProps) => {
  const { t } = useTranslation('accountHome')
  const { fireEvent } = useEvent('accountHome')

  const { data, isError, isLoading } = usePlansQuery()

  const omCombined = useCombinedPartAPartB({
    summary: data?.summary,
    plans: data?.plans
  })

  // This governs display of alternate messaging
  const noPlans =
    !isLoading &&
    !isError &&
    !data?.plans.length &&
    !data?.summary.hasPartA &&
    !data?.summary.hasPartB

  const filteredPlans = useMemo(() => {
    if (!omCombined) return data?.plans

    const filtered =
      data?.plans.filter(
        (plan) =>
          ![CoveragePlanType.OmPartA, CoveragePlanType.OmPartB].includes(
            plan.type
          )
      ) || []

    return [...filtered]
  }, [data?.plans, omCombined])

  const planSummaryLink = useMedicareUrl(
    'https://www.medicare.gov/plan-compare/#/',
    'https://www.medicare.gov/plan-compare/#/?lang=es'
  )

  const planDetailsLink = useMedicareUrl(
    'https://www.medicare.gov/mbp/plansandcoverage.aspx',
    'https://www.medicare.gov/mbp/plansandcoverage.aspx?Lang=es-mx'
  )

  function planCardEventHandler (
    planType: CoveragePlanType | 'om'
  ): MyLinkClickHandler {
    return ({ linkText, linkUrl }) => {
      fireEvent('accounthome_plancard_click', {
        linkText,
        linkUrl,
        planType
      })
    }
  }

  return (
    <section className={`account-home-section current-plans ${className}`}>
      <div className="icon">
        <IconCheckShield />
      </div>
      <h2 className="ds-text-heading--xl">{t('currentPlans.heading')}</h2>
      <div className="content my-u-pb--32">
        {noPlans
          ? (
          <NoPlansCard className={classes.statusCard} />
            )
          : (
          <div>
            {isLoading && (
              <div className={classes.grid}>
                {Array.from({ length: 3 }).map((x, i) => (
                  <PlanCardSkeleton key={i} />
                ))}
              </div>
            )}
            {isError && <PlansErrorAlert className={classes.statusCard} />}
            {filteredPlans && (
              <div className={classes.grid}>
                {omCombined && (
                  <PartAPartBPlanCard
                    partAEffectiveDate={omCombined.partAEffectiveDate}
                    partBEffectiveDate={omCombined.partBEffectiveDate}
                    urls={omCombined.urls}
                    onLinkClick={planCardEventHandler('om')}
                  />
                )}
                {filteredPlans.map((plan) => (
                  <PlanCard
                    name={plan.name}
                    startDate={plan.enrollment_effective_date}
                    title={plan.title}
                    type={plan.type}
                    urls={plan.urls}
                    onLinkClick={planCardEventHandler(plan.type)}
                  />
                ))}
              </div>
            )}
          </div>
            )}

        {!isLoading && !noPlans && (
          <div className="ds-u-margin-top--3 ds-u-display--flex my-u-gap--8 ds-u-flex-direction--column ds-u-md-flex-direction--row">
            <MyLink
              className="ds-c-button ds-c-button--solid ds-c-button--alternate ds-u-display--block ds-u-md-display--inline-block"
              href={planSummaryLink}
              onClick={({ linkText, linkUrl }) =>
                fireEvent('accounthome_checkplansummary_click', {
                  linkText,
                  linkUrl
                })
              }
            >
              {t('currentPlans.planSummaryLink')}
            </MyLink>

            <MyLink
              className="ds-c-button ds-u-display--block ds-u-md-display--inline-block"
              href={planDetailsLink}
              onClick={({ linkText, linkUrl }) =>
                fireEvent('accounthome_getplandetails_click', {
                  linkText,
                  linkUrl
                })
              }
            >
              {t('currentPlans.planDetailsLink')}
            </MyLink>
          </div>
        )}
      </div>
    </section>
  )
}
